import { useMutation } from '@apollo/client/react'
import { toast } from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'

import { CrossChannelsTakeawaysDocument } from './useCrossChannelsTakeaways'

export const RefreshCrossChannelsTakeawaysDocument = graphql(/* GraphQL */ `
  mutation RefreshCrossChannelsTakeaways(
    $input: RefreshCrossChannelsTakeawaysInput!
  ) {
    refreshCrossChannelsTakeaways(input: $input) {
      ... on RefreshCrossChannelsTakeawaysSuccess {
        takeaways {
          id
          title
          description
          category
          channelIds
          feedback
          ctas {
            label
            action
            variant
          }
          metadata {
            draftId
            createPostType
            postId
            prefillAiPrompt
          }
        }
      }
      ... on InvalidInputError {
        message
      }
    }
  }
`)

interface UseRefreshCrossChannelsTakeawaysOptions {
  organizationId: string
  channelIds: ReadonlyArray<string>
}

interface UseRefreshCrossChannelsTakeawaysResult {
  refresh: () => Promise<void>
  loading: boolean
}

export function useRefreshCrossChannelsTakeaways({
  organizationId,
  channelIds,
}: UseRefreshCrossChannelsTakeawaysOptions): UseRefreshCrossChannelsTakeawaysResult {
  const input = { organizationId, channelIds: [...channelIds] }
  const [mutate, { loading }] = useMutation(
    RefreshCrossChannelsTakeawaysDocument,
    {
      variables: { input },
      update(cache, { data }) {
        const result = data?.refreshCrossChannelsTakeaways
        if (result?.__typename !== 'RefreshCrossChannelsTakeawaysSuccess') {
          return
        }

        cache.writeQuery({
          query: CrossChannelsTakeawaysDocument,
          variables: { input },
          data: { crossChannelsTakeaways: result.takeaways },
        })
      },
    },
  )

  const refresh = async (): Promise<void> => {
    try {
      const { data } = await mutate()
      const result = data?.refreshCrossChannelsTakeaways
      if (result?.__typename === 'RefreshCrossChannelsTakeawaysSuccess') {
        toast.success('Takeaways refreshed')
        return
      }
      if (result?.__typename === 'InvalidInputError') {
        toast.error(result.message)
        return
      }
      toast.error('Failed to refresh takeaways')
    } catch {
      toast.error('Failed to refresh takeaways')
    }
  }

  return { refresh, loading }
}
